import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import TestimonialSlider from '@/components/TestimonialSlider';
import ContactFormDialog from '@/components/ContactFormDialog';
import { ShoppingBag, Shield, Search, BarChart } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function AmazonBrandRegistry() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    {
      icon: Shield,
      title: 'Brand Protection',
      description: 'Proactively protect your brand from counterfeiters and unauthorized sellers.',
    },
    {
      icon: Search,
      title: 'Enhanced Content',
      description: 'Create rich product descriptions with enhanced brand content and A+ pages.',
    },
    {
      icon: BarChart,
      title: 'Analytics',
      description: 'Access powerful analytics to track brand performance and customer insights.',
    },
    {
      icon: ShoppingBag,
      title: 'Brand Stores',
      description: 'Build a customized shopping experience with your own Amazon Brand Store.',
    },
  ];

  const requirements = [
    'Active registered trademark or pending trademark application',
    'Brand name must appear on products and/or packaging',
    'Amazon selling account in good standing',
    'Ability to verify ownership of the brand',
  ];

  const faqs = [
    {
      question: 'What is Amazon Brand Registry?',
      answer: 'Amazon Brand Registry is a program that provides brand owners with enhanced control over their product listings, powerful tools to protect their intellectual property, and the ability to create an enhanced brand presence on Amazon.',
    },
    {
      question: 'Do I need a registered trademark?',
      answer: 'Yes, a registered trademark is required to enroll in Amazon Brand Registry. The trademark must be active and registered in the country where you wish to enroll. We can help you obtain trademark registration if you don\'t already have one.',
    },
    {
      question: 'What are the benefits of Brand Registry?',
      answer: 'Benefits include proactive brand protection tools, the ability to report violations, access to sponsored brands advertising, enhanced brand content capabilities, and detailed analytics about your brand\'s performance on Amazon.',
    },
    {
      question: 'How long does enrollment take?',
      answer: 'Once you have a registered trademark, the Brand Registry enrollment process typically takes 3-5 business days. We guide you through the entire process to ensure smooth and quick approval.',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="amazon-brand-registry-page">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden" data-testid="service-hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1605647540924-852290f6b0d5?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/90 via-[#06214c]/85 to-[#A0522D]/80"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="service-hero-title"
            >
              Amazon Brand Registry
            </h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8" data-testid="service-hero-subtitle">
              Protect and grow your brand on Amazon with comprehensive Brand Registry assistance. Take control of your listings and build customer trust.
            </p>
            <button
              onClick={() => setShowDialog(true)}
              className="btn-primary"
              data-testid="service-hero-cta"
            >
              Get Started Today
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section section-abstract-bg" data-testid="service-benefits-section">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Why Brand Registry?
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Unlock powerful tools and protection for your brand on Amazon.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in" data-testid={`benefit-${index}`}>
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center">
                      <Icon className="text-white" size={32} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="section bg-[#FFF5E6]" data-testid="service-info-section">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-8 text-center" style={{ fontFamily: 'Playfair Display' }}>
              Take Control of Your Amazon Presence
            </h2>
            <div className="space-y-6 text-lg text-[#2C1810] leading-relaxed">
              <p>
                Amazon Brand Registry is an essential tool for brand owners selling on Amazon. It provides enhanced protection against counterfeiters and unauthorized sellers while giving you greater control over your product listings and brand presentation.
              </p>
              <p>
                With Brand Registry, you can report suspected intellectual property violations, influence product listing details, and access powerful tools like A+ Content and Amazon Stores to create rich, engaging shopping experiences that drive sales and build customer loyalty.
              </p>
              <p>
                Our team assists you through the entire Brand Registry enrollment process, from ensuring you meet all requirements to successfully completing your application. If you don't yet have a registered trademark, we can help you obtain one, as it's a prerequisite for enrollment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="section section-abstract-shapes" data-testid="service-requirements-section">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Enrollment Requirements
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              What you need to enroll in Amazon Brand Registry.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="card">
              <ul className="space-y-4">
                {requirements.map((req, index) => (
                  <li key={index} className="flex items-start space-x-3" data-testid={`requirement-${index}`}>
                    <div className="flex-shrink-0 w-6 h-6 bg-[#06214c] rounded-full flex items-center justify-center text-white text-sm font-bold mt-1">
                      {index + 1}
                    </div>
                    <span className="text-gray-700">{req}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section bg-[#FFF5E6]" data-testid="service-faq-section">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Common questions about Amazon Brand Registry.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger
                    className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]"
                    data-testid={`faq-question-${index}`}
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]" data-testid={`faq-answer-${index}`}>
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Testimonials Slider */}
      <TestimonialSlider autoSlide={true} />

      {/* CTA Section */}
      <section 
        className="section relative overflow-hidden" 
        data-testid="service-cta-section"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1599090738077-75d2187fd892?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#06214c]/95 to-[#A0522D]/95"></div>
        </div>
        <div className="container mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white" style={{ fontFamily: 'Playfair Display' }}>
            Ready to Protect Your Amazon Brand?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-white/90">
            Get enrolled in Amazon Brand Registry and take control of your brand presence today.
          </p>
          <button
            onClick={() => setShowDialog(true)}
            className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl"
            data-testid="service-cta-btn"
          >
            Get Started Today
          </button>
        </div>
      </section>

      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="amazon_brand_registry" />

      <Footer />
    </div>
  );
}